import React, { useState, useEffect, useRef } from "react";
import config from "../../config";
import { EMoveArmDirection } from "../../Model/EMoveImagingArmButton";
import { ENotificationType } from "../../Model/ENotificationType";
import { ImagerState, PsrState } from "../../Model/ModuleState";
import { EArmType } from "../../Model/Waypoint";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { getModuleStateById } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import ArmMover from "./ArmMover";
import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";

const X_SPEED = "X_SPEED";
const Y_SPEED = "Y_SPEED";
const Z_SPEED = "Z_SPEED";

function getSpeed(key: string) {
  const value = localStorage.getItem(key);
  if (value != null) {
    return Number.parseInt(value);
  } else {
    return 3;
  }
}

interface HubRefParams {
  hubConnection: HubConnection | null;
}

const startSignalRConnection = (connection: HubConnection) =>
  connection
    .start()
    .then(() => {
      console.info("Arm SignalR Connected");
    })
    .catch((err) =>
      sendNotification(
        ENotificationType.Error,
        "Arm SignalR Connection Error: " + err
      )
    );

type Props = {
  moduleIdentifier: string;
  armType: EArmType;
};

export default function CartesianArmMover({
  moduleIdentifier,
  armType,
}: Props) {
  const [xSpeed, setXSpeed] = useState(getSpeed(X_SPEED));
  const [ySpeed, setYSpeed] = useState(getSpeed(Y_SPEED));
  const [zSpeed, setZSpeed] = useState(getSpeed(Z_SPEED));

  const hubRef = useRef<HubRefParams>({ hubConnection: null });

  useEffect(() => {
    localStorage.setItem(X_SPEED, xSpeed.toString());
  }, [xSpeed]);

  useEffect(() => {
    localStorage.setItem(Y_SPEED, ySpeed.toString());
  }, [ySpeed]);

  useEffect(() => {
    localStorage.setItem(Z_SPEED, zSpeed.toString());
  }, [zSpeed]);

  useEffect(() => {
    const urlConnection = config.SIGNALR_URL + "/arm";
    const hubConnection = new HubConnectionBuilder()
      .withUrl(urlConnection)
      .build();
    hubRef.current.hubConnection = hubConnection;

    hubConnection.onclose(() => {
      console.log("SignalR connection lost");
      setTimeout(() => {
        console.log("SignalR connection restarted");
        if (hubConnection != null) {
          startSignalRConnection(hubConnection);
        }
      }, 5000);
    });

    startSignalRConnection(hubConnection);

    return () => {
      hubConnection?.stop();
    };
  }, []);

  const moduleStateById = useTypedSelector(
    (state) => getModuleStateById(state)[moduleIdentifier]
  );
  const disabledArrow  = useTypedSelector((state) => state.moduleStateStore.disabledArrow)
  const moduleState = armType === EArmType.ImagingArm ? moduleStateById?.imager : moduleStateById?.psr;
  if (moduleState == null) {
    throw new Error("Module State is null");
  }

  const getDirectionalSpeed = (direction: EMoveArmDirection) => {
    switch (direction) {
      case EMoveArmDirection.East:
      case EMoveArmDirection.West:
        return xSpeed;

      case EMoveArmDirection.North:
      case EMoveArmDirection.South:
        return ySpeed;

      case EMoveArmDirection.Up:
      case EMoveArmDirection.Down: {
        return zSpeed;
      }
    }
    return 1;
  };

  const onButtonDown = (direction: EMoveArmDirection) => {
    if(!disabledArrow) {
      hubRef.current.hubConnection?.send(
        "ArmMoveToTravelLimit",
        moduleIdentifier,
        armType,
        direction,
        getDirectionalSpeed(direction)
      );
    }
  };

  const onButtonUp = () => {
    if(!disabledArrow) {
      hubRef.current.hubConnection?.send("ArmStop", moduleIdentifier);
    }
  };

  return ArmMover.getCartesianArmMover(
    {
      armName: EArmType[armType],
      axisLabel: ["X", "Y", "Z"],
      onButtonDown: onButtonDown,
      onButtonUp: onButtonUp,
      x: armType === EArmType.ImagingArm ? (moduleState as ImagerState).imagingArmPosition.x : (moduleState as PsrState).cartesianPosition.x,
      y: armType === EArmType.ImagingArm ? (moduleState as ImagerState).imagingArmPosition.y : (moduleState as PsrState).cartesianPosition.y,
      z: armType === EArmType.ImagingArm ? (moduleState as ImagerState).imagingArmPosition.z : (moduleState as PsrState).cartesianPosition.z,
      xSpeed: xSpeed,
      ySpeed: ySpeed,
      zSpeed: zSpeed,
      setXSpeed: setXSpeed,
      setYSpeed: setYSpeed,
      setZSpeed: setZSpeed
    }
  );
}
